const toBase64UrlBytes = value => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
  const decoded = window.atob(padded)
  return Uint8Array.from(decoded, character => character.charCodeAt(0))
}

const DEVICE_ID_STORAGE_KEY = 'vimc.webpush.deviceId'

function getDeviceId() {
  try {
    let deviceId = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY)
    if (!deviceId) {
      const bytes = window.crypto.getRandomValues(new Uint8Array(16))
      deviceId = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
      window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId)
    }
    return deviceId
  } catch (error) {
    console.error('[WebPush] Could not persist the browser device ID.', error)
    return null
  }
}

async function getDeviceInfo() {
  const userAgent = navigator.userAgent || ''
  const userAgentData = navigator.userAgentData
  const platform = (userAgentData && userAgentData.platform) || navigator.platform || ''
  let deviceModel = ''
  if (userAgentData && userAgentData.getHighEntropyValues) {
    try {
      const highEntropyValues = await userAgentData.getHighEntropyValues(['model'])
      deviceModel = highEntropyValues.model || ''
    } catch (error) {
      console.warn('[WebPush] Could not read device model from browser metadata.', error)
    }
  }
  const isIPad =
    /iPad/i.test(userAgent) ||
    (platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isIPhone = /iPhone|iPod/i.test(userAgent)
  const isAndroid = /Android/i.test(userAgent) || /Android/i.test(platform)
  const isTablet =
    (userAgentData && userAgentData.mobile === false && /Android/i.test(userAgent)) ||
    /Tablet|iPad/i.test(userAgent) ||
    isIPad ||
    (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent))

  let os = 'Unknown'
  if (isIPad || isIPhone || /iOS/i.test(platform)) os = 'iOS'
  else if (isAndroid) os = 'Android'
  else if (/Windows/i.test(platform) || /Windows/i.test(userAgent)) os = 'Windows'
  else if (/Mac/i.test(platform) || /Mac OS/i.test(userAgent)) os = 'macOS'
  else if (/Chrome OS|CrOS/i.test(userAgent)) os = 'Chrome OS'
  else if (/Linux/i.test(platform) || /Linux/i.test(userAgent)) os = 'Linux'

  let deviceType = 'DESKTOP'
  if (isTablet) deviceType = 'TABLET'
  else if (isIPhone || (isAndroid && /Mobile/i.test(userAgent)) ||
      (userAgentData && userAgentData.mobile)) deviceType = 'MOBILE'

  let browser = 'Unknown'
  if (/EdgA|EdgiOS|Edg\//i.test(userAgent)) browser = 'Edge'
  else if (/SamsungBrowser/i.test(userAgent)) browser = 'Samsung Internet'
  else if (/OPR|Opera/i.test(userAgent)) browser = 'Opera'
  else if (/CriOS/i.test(userAgent)) browser = 'Chrome iOS'
  else if (/Chrome|Chromium/i.test(userAgent)) browser = 'Chrome'
  else if (/FxiOS/i.test(userAgent)) browser = 'Firefox iOS'
  else if (/Firefox/i.test(userAgent)) browser = 'Firefox'
  else if (/Safari/i.test(userAgent)) browser = 'Safari'

  const deviceName = [deviceModel, browser, 'on', os, deviceType.toLowerCase()]
    .filter(Boolean)
    .join(' ')
  return { deviceName, deviceType, os, browser, userAgent }
}

export async function subscribeToWebPush(publicKey) {
  if (
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    console.warn('[WebPush] Push notifications are not supported.')
    return null
  }
  if (!publicKey) {
    console.error('[WebPush] VAPID public key is not configured.')
    return null
  }

  try {
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return null
    } else if (Notification.permission !== 'granted') {
      return null
    }

    const serviceWorkerUrl = `${process.env.PUBLIC_URL || ''}/firebase-messaging-sw.js`
    const registration = await navigator.serviceWorker.register(serviceWorkerUrl)
    await navigator.serviceWorker.ready

    const applicationServerKey = toBase64UrlBytes(publicKey)
    let subscription = await registration.pushManager.getSubscription()
    const existingKey = subscription?.options?.applicationServerKey
    const existingKeyBytes = existingKey ? new Uint8Array(existingKey) : null
    const sameApplicationKey =
      existingKeyBytes &&
      existingKeyBytes.length === applicationServerKey.length &&
      applicationServerKey.every((byte, index) => byte === existingKeyBytes[index])

    if (subscription && !sameApplicationKey) {
      await subscription.unsubscribe()
      subscription = null
    }
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      })
    }

    const p256dh = subscription.getKey('p256dh')
    const auth = subscription.getKey('auth')
    if (!p256dh || !auth) {
      throw new Error('Browser returned a subscription without encryption keys.')
    }

    const encode = bytes =>
      window
        .btoa(String.fromCharCode.apply(null, new Uint8Array(bytes)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
    return {
      endpoint: subscription.endpoint,
      p256dh: encode(p256dh),
      auth: encode(auth),
      deviceId: getDeviceId(),
      platform: 'web',
      ...(await getDeviceInfo()),
    }
  } catch (error) {
    console.error('[WebPush] Could not register the push subscription.', error)
    return null
  }
}

export async function unsubscribeFromWebPush() {
  if (!('serviceWorker' in navigator)) return null
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    const subscription = registration
      ? await registration.pushManager.getSubscription()
      : null
    if (!subscription) return null

    const endpoint = subscription.endpoint
    await subscription.unsubscribe()
    return endpoint
  } catch (error) {
    console.error('[WebPush] Could not unsubscribe from push notifications.', error)
    return null
  }
}
